import moment from "moment-timezone";
import {
  Archive as ArchiveXIcon,
  CheckIcon,
  CopyIcon,
  TrashIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CommonDestinationTimezone, createDateLabel } from "@/lib/utils";
import { CommodityOptions } from "@/lib/api/commodity";
import { ContractSourceType } from "@/lib/api/generalContract";
import { dateWithFormatSort } from "@/components/ui/DataTable/sorting";
import type { SortingFn } from "@tanstack/react-table";
import type { DataTableColumn } from "@/components/ui/DataTable";

/**
 * Sorting function that only cares about null/falsy vs truthy.
 * Nulls/falsy sort first (asc), truthy values are treated as equal (returns 0)
 * so the next sort column in the chain takes over.
 */
const nullsFirstSort: SortingFn<any> = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  const aEmpty = !a;
  const bEmpty = !b;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;
  return 0;
};

const SourceTypeOptions = [
  { value: ContractSourceType.SpeechInput, label: "Spoken" },
  { value: ContractSourceType.GeneralInput, label: "Form" },
  { value: ContractSourceType.TtFill, label: "TT Fill" },
];

function formatMarketingFee(value: string | undefined | null): string {
  switch (value) {
    case "1.5":
      return "1.5%";
    case "1.0":
      return "1%";
    case "0.0":
    case "None":
    default:
      return "None";
  }
}

export function getGeneralContractColumns(options?: {
  showArchiveButton?: boolean;
  showReviewButton?: boolean;
  showDeleteButton?: boolean;
  onDuplicate?: (row: any) => void;
  onArchive?: (id: string) => void;
  onReview?: (id: string) => void;
  onDelete?: (id: string) => void;
}): any[] {
  const {
    showArchiveButton = false,
    showReviewButton = false,
    showDeleteButton = false,
    onDuplicate,
    onArchive,
    onReview,
    onDelete,
  } = options || {};

  const columns: DataTableColumn<any>[] = [];

  // Duplicate (copy) button — always shown
  if (onDuplicate) {
    columns.push({
      id: "duplicate_contract_button",
      header: "",
      cell: ({ row }: any) => (
        <Button variant="ghost" size="sm" onClick={() => onDuplicate(row.original)}>
          <CopyIcon className="h-4 w-4" />
        </Button>
      ),
      size: 50,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    });
  }

  // Archive button (unstaged)
  if (showArchiveButton && onArchive) {
    columns.push({
      id: "archive_contract_button",
      header: "Archive",
      cell: ({ row }: any) => (
        <Button variant="ghost" size="sm" onClick={() => onArchive(row.original.id)}>
          <ArchiveXIcon className="h-4 w-4" />
        </Button>
      ),
      size: 80,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    });
  }

  // Review button (staged)
  if (showReviewButton && onReview) {
    columns.push({
      id: "review_contract_button",
      header: "Reviewed",
      cell: ({ row }: any) => (
        <Button variant="outline" size="sm" onClick={() => onReview(row.original.id)}>
          <CheckIcon className="h-4 w-4" />
        </Button>
      ),
      size: 90,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    });
  }

  // Delete button (staged)
  if (showDeleteButton && onDelete) {
    columns.push({
      id: "delete_contract_button",
      header: "Delete",
      cell: ({ row }: any) => (
        <Button variant="outline" size="sm" onClick={() => onDelete(row.original.id)}>
          <TrashIcon className="h-4 w-4" />
        </Button>
      ),
      size: 80,
      enableSorting: false,
      enableHiding: false,
      enableColumnFilter: false,
      disableRowClick: true,
    });
  }

  // Data columns
  columns.push(
    {
      accessorKey: "created_at",
      header: "Created At",
    columnFilterType: 'date',
    filterDateTimezone: CommonDestinationTimezone,
    filterDisplayFn: (v) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
    filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      accessorFn: (row: any) =>
        row.created_at
          ? row.created_at
          : "",
      cell: (info) => info.getValue() ? moment.tz(info.getValue(), CommonDestinationTimezone).format("hh:mm A MM/DD/YYYY") : ""
    },
    {
      accessorKey: "contract_date",
      header: "Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      size: 130,
      minSize: 90,
      accessorFn: (row: any) => createDateLabel(row.contract_date),
    },
    {
      id: "location_id",
      header: "Location",
      size: 80,
      accessorFn: (row: any) => row.Location?.long_name || "",
    },
    {
      accessorKey: "purchase_sale_type",
      header: "P/S",
    },
    {
      accessorKey: "contract_type",
      header: "Type",
      size: 80,
    },
    {
      id: "customer_id",
      header: "Customer",
      accessorFn: (row: any) => row.Customer?.long_name || "",
    },
    {
      accessorKey: "contract_no",
      header: "Contract #",
    },
    {
      id: "commodity",
      header: "Commodity",
      accessorFn: (row: any) => {
        return (
          CommodityOptions.find(
            (option) =>
              option.value?.toLocaleLowerCase() === row.commodity?.toLocaleLowerCase()
          )?.label || row.commodity
        );
      },
    },
    {
      accessorKey: "crop_year",
      header: "Crop Year",
    },
    {
      accessorKey: "option_month",
      header: "Option Month",
    },
    {
      accessorKey: "reference",
      header: "Reference",
    },
    {
      accessorKey: "quantity",
      header: "Quantity",
      accessorFn: (row: any) => row.quantity?.toString() || "",
    },
    {
      accessorKey: "uom",
      header: "Uom",
    },
    {
      accessorKey: "price_uom",
      header: "Price Uom",
    },
    {
      accessorKey: "currency",
      header: "Currency",
    },
    {
      accessorKey: "basis",
      header: "Basis",
      accessorFn: (row: any) => row.basis?.toString() || "",
    },
    {
      accessorKey: "futures",
      header: "Futures",
      accessorFn: (row: any) => row.futures?.toString() || "",
    },
    {
      accessorKey: "delivery_terms",
      header: "Delivery Terms",
    },
    {
      accessorKey: "comments",
      header: "Comments",
      enableColumnFilter: false,
    },
    {
      id: "market_zone_id",
      header: "Market Zone",
      accessorFn: (row: any) => row.MarketZone?.market_zone || "",
    },
    {
      id: "trader_id",
      header: "Trader",
      accessorFn: (row: any) => row.Trader?.name || "",
    },
    {
      accessorKey: "shipment_start_date",
      header: "Shipment Start Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.shipment_start_date),
    },
    {
      accessorKey: "shipment_end_date",
      header: "Shipment End Date",
      sortingFn: dateWithFormatSort("MM/DD/YYYY"),
      accessorFn: (row: any) => createDateLabel(row.shipment_end_date),
    },
    {
      id: "contract_marketing_fee_setting",
      header: "Marketing Fee",
      accessorFn: (row: any) => formatMarketingFee(row.contract_marketing_fee_setting),
    },
    {
      id: "mz_adj_display",
      header: "MZ ADJ",
      accessorFn: (row: any) => row.MzAdj?.mz_adj || "0",
    },
    {
      id: "add_ded_display",
      header: "Add/Ded ADJ",
      accessorFn: (row: any) => row.AddDedAdj?.amount || "0",
    },
    {
      id: "source_type_display",
      header: "Source",
      accessorFn: (row: any) =>
        SourceTypeOptions.find((item) => item.value === row.source_type)?.label || "",
    },
  );

  // Hidden phantom column for nulls-first sorting on contract_no.
  // Uses a separate id so it can appear as its own entry in defaultSorting
  // while the real contract_no column handles actual value sorting.
  columns.push({
    id: "contract_no_nulls",
    accessorFn: (row: any) => row.contract_no,
    header: "contract_no_nulls",
    sortingFn: nullsFirstSort,
    enableHiding: false,
    enableColumnFilter: false,
  });

  return columns;
}
